import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, pwywRangeRows } from 'utils/utils';
import styles from './PunchupTicketTypeTile.module.css';

export interface PunchupTicketTypeTileProps {
	/** Ticket type display name */
	name: string;
	/** On-sale status label (e.g. "On sale", "Off sale · starts Jan 1") */
	status?: string;
	/** Face-value price in dollars. Shown as "$X.XX". Hidden when 0 or undefined. */
	initialPrice?: number;
	/** When true, shows the PWYW price range (if known) in the orange price slot, else "Pay what you want" */
	isPayWhatYouWant?: boolean;
	/** PWYW floor price — combined with maxPrice into a "from $X" / "up to $Y" range in the orange price slot. */
	minPrice?: number | null;
	/** PWYW ceiling price — see minPrice. */
	maxPrice?: number | null;
	/** Extra description lines rendered below the name (e.g. "12 / 100 sold · 3 comped") */
	descriptions?: string[];
	/** "Currently held by" email — renders a muted line below the name */
	ownerEmail?: string;
	/**
	 * Ticket-type colour code, shown as a thin left-edge rail. Per-ticket data — pass the resolved hex
	 * from `useTicketTypeColors`. Omitted (e.g. the Transfer dialogue) renders no rail.
	 */
	color?: string;
	/** Makes the tile an interactive button */
	onClick?: () => void;
	dataCy?: string;
}

export const PunchupTicketTypeTile: FC<PunchupTicketTypeTileProps> = ({
	name,
	status,
	initialPrice,
	isPayWhatYouWant = false,
	minPrice,
	maxPrice,
	descriptions,
	ownerEmail,
	color,
	onClick,
	dataCy,
}) => {
	const { t } = useTranslation();

	// Left colour rail — the ticket type's colour code on the tile. Colour is per-ticket data (inline);
	// shape comes from the shared .colorRail utility. Nothing renders when no colour is given.
	const colorRail = color ? <span className={styles.colorRail} style={{ backgroundColor: color }} aria-hidden /> : null;

	// PWYW: the orange price slot shows the range as two right-aligned rows — a muted "from"/"up to"
	// connector + the orange amount (e.g. "from $50" / "up to $99"). Fixed price → "$X"; a free ticket
	// (initialPrice 0) → "Free" in orange. The muted "Pay what you want" line is only the PWYW
	// fallback when no min/max is known (e.g. the Transfer dialogue, which omits them).
	const rangeRows = isPayWhatYouWant ? pwywRangeRows(minPrice, maxPrice) : [];
	const showPwywFallback = isPayWhatYouWant && rangeRows.length === 0;
	const fixedPrice = !isPayWhatYouWant && initialPrice != null && initialPrice > 0 ? initialPrice : undefined;
	const isFree = !isPayWhatYouWant && initialPrice === 0;
	const singlePrice =
		fixedPrice != null ? `$${formatMoney(fixedPrice)}` : isFree ? t('ticket-free', 'Free') : undefined;

	const content = (
		<>
			<div className={styles.info}>
				{status && <span className={styles.status}>{status}</span>}
				<span className={styles.name}>{name}</span>
				{showPwywFallback && <span className={styles.meta}>{t('pay-what-you-want', 'Pay what you want')}</span>}
				{descriptions?.map((line, i) => (
					<span key={i} className={styles.meta}>
						{line}
					</span>
				))}
				{ownerEmail && (
					<span className={styles.meta}>
						{t('currently-held-by', 'Currently held by')} {ownerEmail}
					</span>
				)}
			</div>
			{rangeRows.length > 0 ? (
				<span className={styles.priceRange}>
					{rangeRows.map((row, i) => (
						<span key={i} className={styles.priceRangeRow}>
							<span className={styles.priceRangeConnector}>{row.connector}</span>
							<span className={styles.priceRangeAmount}>{row.amount}</span>
						</span>
					))}
				</span>
			) : (
				singlePrice && <span className={styles.price}>{singlePrice}</span>
			)}
		</>
	);

	if (onClick) {
		return (
			<button type="button" className={styles.tileButton} onClick={onClick} data-cy={dataCy}>
				{colorRail}
				{content}
			</button>
		);
	}

	return (
		<div className={styles.tile} data-cy={dataCy}>
			{colorRail}
			{content}
		</div>
	);
};
